import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ConfirmDocumentResponse,
  DocumentByIdRequest,
  DocumentEmbedResponse,
  DocumentMetadataResponse,
  ENVIRONMENT,
  LoadDocumentsResponse,
  LoadRequestedDocumentsResponse,
  RequestedDocument,
  UploadDocumentRequest,
  UploadProgressResponse,
} from '@ld.consumer-portal/models';
import { combineLatest, firstValueFrom, Observable, of, scan, switchMap, throwError } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class DocumentRequestsService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async loadDocumentHistory(loanId: string): Promise<LoadDocumentsResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/documents`;

    try {
      const res = await firstValueFrom(this.http.get<LoadDocumentsResponse>(url));
      return {
        documents: res.documents.map((doc, index) => ({
          ...doc,
          uniqId: doc.uniqId || `doc-${doc.documentId}-${index}`,
        })),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error loading document history for loan ${loanId}`);
      throw new Error(`Failed to load document history: ${message}`);
    }
  }

  async loadTodos(loanId: string): Promise<LoadRequestedDocumentsResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/requests`;

    try {
      const res = await firstValueFrom(this.http.get<LoadRequestedDocumentsResponse>(url));
      return {
        requests: res.requests.map((req, index) => ({
          ...req,
          uniqId: req.uniqId || `req-${req.requestId}-${index}`,
        })),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error loading todos for loan ${loanId}`);
      throw new Error(`Failed to load todos: ${message}`);
    }
  }

  uploadForRequest(loanId: string, request: UploadDocumentRequest): Observable<UploadProgressResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/requests/${request.todo.requestId}`;
    return this.uploadDocument('PUT', url, request);
  }

  uploadNewDocument(loanId: string, request: UploadDocumentRequest): Observable<UploadProgressResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/requests`;
    return this.uploadDocument('POST', url, request);
  }

  postUpload(loanId: string): void {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/postupload`;
    this.http.patch(url, {}).subscribe({
      error: (error) =>
        this.loggingService.logException(error, `Error in post-upload for loan ${loanId}`),
    });
  }

  async getDocumentEmbed(loanId: string, request: DocumentByIdRequest): Promise<DocumentEmbedResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/documents/${request.documentId}/embed`;

    const params: Record<string, string> = {};
    if (request.borrowerId) {
      params['borrowerId'] = request.borrowerId;
    }
    if (request.ssnLastFour) {
      params['ssnLastFour'] = request.ssnLastFour;
    }

    try {
      return await firstValueFrom(this.http.get<DocumentEmbedResponse>(url, { params }));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting document embed for ${request.documentId}`);
      throw new Error(`Failed to get document embed: ${message}`);
    }
  }

  async confirmDocument(loanId: string, request: DocumentByIdRequest): Promise<ConfirmDocumentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/documents/${request.documentId}/complete`;

    try {
      await firstValueFrom(this.http.put<void>(url, request));
      return { ...request, confirmed: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error confirming document ${request.documentId}`);
      throw new Error(`Failed to confirm document: ${message}`);
    }
  }

  async getDocumentMetadata(documentId: string): Promise<DocumentMetadataResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/documents/${documentId}/metadata`;

    try {
      return await firstValueFrom(this.http.get<DocumentMetadataResponse>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting document metadata for ${documentId}`);
      throw new Error(`Failed to get document metadata: ${message}`);
    }
  }

  async viewDocumentVerifyAndConsent(loanId: string, ssn: string, documentId: string): Promise<Blob> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/documents/${documentId}/view`;

    try {
      return await firstValueFrom(this.http.post(url, { loanNumber: loanId, ssn }, { responseType: 'blob' }));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error viewing document ${documentId}`);
      throw new Error(`Failed to view document: ${message}`);
    }
  }

  async deleteAssetVerification(loanId: string, borrowerId: string): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/requests/borrowers/${borrowerId}/assetverification`;

    try {
      await firstValueFrom(this.http.delete<void>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error deleting asset verification for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to delete asset verification: ${message}`);
    }
  }

  private uploadDocument(
    method: 'PUT' | 'POST',
    url: string,
    request: UploadDocumentRequest,
  ): Observable<UploadProgressResponse> {
    if (request.fileList.length === 0) {
      return throwError(() => new Error('No files to upload'));
    }

    const progressObservables: Observable<UploadProgressResponse>[] = [];

    for (let i = 0; i < request.fileList.length; i++) {
      const formData = new FormData();
      formData.append('notes', request.notesList[i] || '');
      formData.append('file', request.fileList[i]);

      const uploadRequest = new HttpRequest(method, url, formData, { reportProgress: true });

      const uploadObservable = this.http
        .request<RequestedDocument>(uploadRequest)
        .pipe(scan((acc, event) => this.progressAccumulator(acc, event), this.progressAccumulatorSeed(request)));

      progressObservables.push(uploadObservable);
    }

    return combineLatest(progressObservables).pipe(
      switchMap((progresses) => {
        for (const progress of progresses) {
          if (
            progress.event &&
            typeof progress.event === 'object' &&
            'type' in progress.event &&
            (progress.event.type === HttpEventType.UploadProgress || progress.event.type === HttpEventType.Sent)
          ) {
            return of(progress);
          }
        }
        return of(progresses[0]);
      }),
    );
  }

  private progressAccumulatorSeed(request: UploadDocumentRequest): UploadProgressResponse {
    let totalSize = 0;
    for (const file of request.fileList) {
      totalSize += file.size;
    }

    const now = new Date();
    return {
      progress: 0,
      loaded: 0,
      total: totalSize,
      currentBPMS: 0,
      startTime: now,
      updateTime: now,
      estimatedFinish: now,
      rollingAvg: 1000,
      todo: request.todo,
      event: {},
    };
  }

  private progressAccumulator(acc: UploadProgressResponse, event: unknown): UploadProgressResponse {
    const now = new Date();
    const nowTs = Date.now();

    if (!event || typeof event !== 'object') {
      return acc;
    }

    const httpEvent = event as { type: HttpEventType; loaded?: number; total?: number };

    switch (httpEvent.type) {
      case HttpEventType.Sent:
        return { ...acc, event };

      case HttpEventType.UploadProgress: {
        const loaded = httpEvent.loaded || 0;
        const total = httpEvent.total || acc.total;
        const currentBPMS = loaded / (nowTs - acc.updateTime.getTime());
        const rollingAvg = this.rollingAverage(currentBPMS, acc.rollingAvg === 0 ? currentBPMS : acc.rollingAvg);

        return {
          ...acc,
          event,
          rollingAvg,
          currentBPMS,
          updateTime: now,
          estimatedFinish: new Date(nowTs + (total - loaded) / rollingAvg),
          progress: total ? Math.floor((loaded / total) * 100) : 0,
        };
      }

      case HttpEventType.Response:
        return {
          ...acc,
          event,
          progress: 100,
          loaded: acc.total,
          updateTime: now,
          estimatedFinish: now,
        };

      default:
        return acc;
    }
  }

  private rollingAverage(current: number, previous: number, weight = 0.7): number {
    return weight * current + (1 - weight) * previous;
  }
}
